import { motion } from "motion/react";
import { useLocation, useOutletContext } from "react-router-dom";
import { useState, useEffect } from "react";
import { AnimatedDivider } from "../components/AnimatedDivider";
import { Helmet } from "react-helmet-async";

function WhiteBar() {
  return (
    <motion.div 
      initial={{ scaleX: 0 }}
      whileInView={{ scaleX: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 1.5, ease: [0.19, 1, 0.22, 1] }}
      className="h-[3px] w-full bg-white my-16 origin-left" 
    />
  );
}

export function Apparatus() {
  const location = useLocation();
  const { hasLoaded, handleNavClick, isScrolledPop } = useOutletContext<{ hasLoaded: boolean, handleNavClick: (e: React.MouseEvent<HTMLAnchorElement>, href: string) => void, isScrolledPop?: boolean }>();
  const [isDocking, setIsDocking] = useState(() => Boolean(hasLoaded && location.state?.fromNav));

  useEffect(() => {
    if (isDocking) {
      const timer = setTimeout(() => setIsDocking(false), 500);
      return () => clearTimeout(timer);
    }
  }, [isDocking]);

  return (
    <>
      <Helmet>
        <title>Apparatus • Atharva Dhekne | tacitonic</title>
        <meta name="description" content="A comprehensive list of mastering studio apparatus, including critical listening monitors, analog EQs, compressors, and high-end converters." />
        <meta name="robots" content="index, follow, max-image-preview:none" />
        <meta name="googlebot" content="index, follow, max-image-preview:none" />
        <link rel="canonical" href="https://tacitonic.com/apparatus" />
        <meta property="og:title" content="Apparatus • Atharva Dhekne | tacitonic" />
        <meta property="og:description" content="A comprehensive list of mastering studio apparatus, including critical listening monitors, analog EQs, compressors, and high-end converters." />
        <meta property="og:url" content="https://tacitonic.com/apparatus" />
        <meta name="twitter:card" content="summary" />
      </Helmet>
      <main className="pt-40 pb-32 px-6 max-w-5xl mx-auto min-h-screen">
      <div className="mb-16 pb-8 relative">
        <motion.h1 
          layoutId={isScrolledPop ? undefined : `page-title-Apparatus`}
          initial={(!hasLoaded || isScrolledPop) ? { opacity: 0, scale: 0.95, y: 10 } : undefined}
          animate={{ opacity: 1, scale: 1, y: 0, color: "#FEFDF9" }}
          className="text-[31px] md:text-[52px] lg:text-[64px] font-bold tracking-tighter uppercase leading-none font-display w-fit origin-left text-white"
          transition={{ 
            duration: 1.2,
            ease: [0.16, 1, 0.3, 1],
            layout: { duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] }
          }}
        >
          Apparatus
        </motion.h1>
        {!isDocking && (
          <div className="absolute bottom-0 left-0 w-full">
            <AnimatedDivider className="h-[3px] bg-white" />
          </div>
        )}
      </div>

      <div className={`transition-opacity duration-700 delay-300 ${isDocking ? 'opacity-0' : 'opacity-100'}`}>
        <motion.div
          initial={false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
        <div className="space-y-16">
          <section>
            <h2 className="text-[31px] md:text-[52px] font-bold uppercase tracking-tight mb-8 font-display">Hardware</h2>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              {/* Computing - Large Bento Box */}
              <div className="md:col-span-4 p-4 md:p-8 border-2 border-white/20 hover:border-white transition-all duration-300 group relative overflow-hidden">
                <div className="relative z-10">
                  <h3 className="text-sm font-mono uppercase tracking-[0.2em] opacity-40 mb-6">01 / Computing</h3>
                  <div className="space-y-6">
                    <div className="text-[25px] md:text-[31px] font-bold font-display uppercase tracking-tighter">MacBook Pro 16 2021</div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 opacity-80">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest opacity-40 font-mono mb-1">CPU</div>
                        <div className="text-sm font-bold">M1 Max 10-core</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest opacity-40 font-mono mb-1">GPU</div>
                        <div className="text-sm font-bold">32-core GPU / 16-core NE</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest opacity-40 font-mono mb-1">Memory</div>
                        <div className="text-sm font-bold">32 GB</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Audio + Outboard - Vertical Bento Box */}
              <div className="md:col-span-2 md:row-span-2 p-4 md:p-8 border-2 border-white/20 hover:border-white transition-all duration-300 group">
                <h3 className="text-sm font-mono uppercase tracking-[0.2em] opacity-40 mb-6">02 / Audio + Outboard</h3>
                <ul className="space-y-4 text-[17px] font-bold font-display uppercase tracking-tight">
                  <li className="hover:translate-x-2 transition-transform">Solid State Logic</li>
                  <li className="hover:translate-x-2 transition-transform">Rupert Neve Designs</li>
                  <li className="hover:translate-x-2 transition-transform">API Audio</li>
                  <li className="hover:translate-x-2 transition-transform">Sontec</li>
                  <li className="hover:translate-x-2 transition-transform">Terry Audio</li>
                </ul>
              </div>

              {/* Monitoring - Wide Bento Box */}
              <div className="md:col-span-4 p-4 md:p-8 border-2 border-white/20 hover:border-white transition-all duration-300 group">
                <h3 className="text-sm font-mono uppercase tracking-[0.2em] opacity-40 mb-6">03 / Monitoring</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <ul className="space-y-2 text-lg font-responsive-bold opacity-90">
                    <li>ATC SCM110ASL</li>
                    <li>ATC SCM150ASL</li>
                    <li>KEF Q350</li>
                  </ul>
                  <ul className="space-y-2 text-sm opacity-60 font-mono uppercase tracking-wider">
                    <li>Sennheiser HD 300 Pro</li>
                    <li>Beyerdynamic DT 990 Pro</li>
                    <li>Apple AirPods Max</li>
                    <li>Sony WF-1000XM4</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>



          <section>
            <h2 className="text-[31px] md:text-[52px] font-bold uppercase tracking-tight mb-8 font-display">Software</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* OS + DAW */}
              <div className="p-4 md:p-8 border-2 border-white/20 hover:border-white transition-all duration-300">
                <h3 className="text-sm font-mono uppercase tracking-[0.2em] opacity-40 mb-6">04 / OS + DAW</h3>
                <ul className="space-y-2 text-[15px] font-bold font-display uppercase tracking-tight">
                  <li className="text-xs font-mono opacity-40 mb-2">macOS / Windows 11 Pro / Linux</li>
                  <li>MAGIX Sequoia</li>
                  <li>Merging Technologies Pyramix</li>
                  <li>Ableton Live</li>
                  <li>Apple Logic Pro X</li>
                  <li>Avid Pro Tools</li>
                  <li className="text-sm opacity-60">Native Instruments Maschine / Max/MSP</li>
                  <li className="text-sm opacity-60">Unity / Unreal / Wwise</li>
                </ul>
              </div>

              {/* Plugins - Large Box */}
              <div className="md:col-span-2 p-4 md:p-8 border-2 border-white/20 hover:border-white transition-all duration-300">
                <h3 className="text-sm font-mono uppercase tracking-[0.2em] opacity-40 mb-6">05 / Plugins</h3>
                <div className="flex flex-wrap gap-x-8 gap-y-4 text-[17px] font-bold font-display uppercase tracking-tighter">
                  <span>DMG Audio</span>
                  <span className="opacity-40">Dolby</span>
                  <span>Eventide</span>
                  <span className="opacity-40">FabFilter</span>
                  <span>Metric Halo</span>
                  <span className="opacity-40">Native Instruments / iZotope</span>
                  <span>oeksound</span>
                  <span className="opacity-40">Sonnox</span>
                  <span>Sontec</span>
                  <span className="opacity-40">Universal Audio</span>
                  <span>Waves</span>
                  <span className="opacity-40">Weiss</span>
                </div>
              </div>
            </div>
          </section>
        </div>
      </motion.div>
      </div>
    </main>
    </>
  );
}
